<template>
	<div class="user-avatar-wrapper">
		<div class="user-avatar" :class="{ 'logged-in': isLogin }" @click="handleClick">
			<template v-if="isLogin">
				<!-- 显示手机号后四位或用户名首字母 -->
				<span class="user-info">
					{{ displayName }}
				</span>
			</template>
			<template v-else>
				<!-- 未登录显示灰色小人头像 -->
				<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
						d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
				</svg>
			</template>
		</div>

		<!-- 用户菜单 -->
		<transition name="menu">
			<div v-if="showMenu && isLogin" class="user-menu" @click.stop>
				<div class="menu-header">
					<div class="menu-name">{{ userName }}</div>
					<div class="menu-phone">{{ userPhone }}</div>
				</div>
				<div class="menu-divider"></div>

				<!-- 系统设置按钮 -->
				<button class="menu-item setting-item" @click="handleOpenSettings">
					<span>⚙️</span>
					<span>系统设置</span>
				</button>
				
				<button class="menu-item setting-item" @click="handleRechare">
					<span>⚡</span>
					<span>充值中心</span>
				</button>
				<button class="menu-item setting-item" @click="handleUser">
					<span>👤</span>
					<span>用户列表</span>
				</button>

				<button class="menu-item logout-item" @click="handleLogout">
					<span>🚪</span>
					<span>退出登录</span>
				</button>
			</div>
		</transition>

		<!-- 系统设置弹窗 - 自定义实现 -->
		<transition name="modal">
			<div v-if="showSettingsDialog" class="modal-overlay" @click.self="handleCloseSettings">
				<div class="modal-dialog" @click.stop>
					<div class="modal-header">
						<h3 class="modal-title">系统设置</h3>
						<button class="modal-close" @click="handleCloseSettings">
							<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
									d="M6 18L18 6M6 6l12 12"></path>
							</svg>
						</button>
					</div>

					<div class="modal-body">
						<form @submit.prevent="handleSaveSettings" class="settings-form">
							<div class="form-group">
								<label for="username" class="form-label">
									<span class="label-text">用户名</span>
									<span class="label-required">*</span>
								</label>
								<div class="input-wrapper">
									<span class="input-prefix">👤</span>
									<input id="username" v-model="settingsForm.username" type="text" class="form-input"
										:class="{ 'error': errors.username }" placeholder="请输入用户名" maxlength="20"
										@blur="validateUsername" @input="clearError('username')">
									<span v-if="settingsForm.username" class="char-count">
										{{ settingsForm.username.length }}/20
									</span>
								</div>
								<div v-if="errors.username" class="error-message">
									{{ errors.username }}
								</div>
								<div class="form-tip">
									用户名将显示在您的个人资料中
								</div>
							</div>
						</form>
					</div>

					<div class="modal-footer">
						<button class="btn btn-default" @click="handleCloseSettings">
							取消
						</button>
						<button class="btn btn-primary" :disabled="saveLoading || !isFormValid"
							@click="handleSaveSettings">
							<span v-if="saveLoading" class="loading-spinner"></span>
							{{ saveLoading ? '保存中...' : '保存' }}
						</button>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
	export default {
		name: 'UserAvatar',

		data() {
			return {
				showMenu: false,
				isLogin: false,
				userData: null,
				showSettingsDialog: false,
				saveLoading: false,
				settingsForm: {
					username: ''
				},
				errors: {
					username: ''
				}
			}
		},

		computed: {
			// 显示名称逻辑
			displayName() {
				if (!this.userData) return ''

				const phone = this.userData.user?.phonenumber
				const name = this.userData.user?.name

				// 优先显示手机号后四位
				if (phone && phone.length >= 4) {
					return phone.slice(-4)
				}
				// 其次显示用户名首字母
				if (name) {
					return name.charAt(0).toUpperCase()
				}
				// 默认显示
				return '用户'
			},

			userName() {
				return this.userData?.user?.name || '用户(user)'
			},

			userPhone() {
				const phone = this.userData?.user?.phonenumber
				if (phone && phone.length === 11) {
					// 格式化手机号：138****1234
					return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
				}
				return phone || '未绑定手机号'
			},

			// 表单是否有效
			isFormValid() {
				return this.settingsForm.username.trim() && !this.errors.username
			}
		},

		mounted() {
			// 初始化登录状态
			this.checkLoginStatus()

			// 添加全局点击事件监听
			document.addEventListener('click', this.handleClickOutside)

			// 监听storage变化（用于多标签页同步）
			window.addEventListener('storage', this.handleStorageChange)
		},

		beforeDestroy() {
			// 清理事件监听
			document.removeEventListener('click', this.handleClickOutside)
			window.removeEventListener('storage', this.handleStorageChange)
		},

		methods: {
			// 检查登录状态
			checkLoginStatus() {
				try {
					const currentUser = sessionStorage.getItem('currentUser')
					if (currentUser) {
						this.userData = JSON.parse(currentUser)
						this.isLogin = true
						console.log('用户已登录:', this.userData)
					} else {
						this.isLogin = false
						this.userData = null
					}
				} catch (error) {
					console.error('解析用户数据失败:', error)
					this.isLogin = false
					this.userData = null
				}
			},

			// 处理storage变化
			handleStorageChange(e) {
				if (e.key === 'currentUser') {
					this.checkLoginStatus()
				}
			},

			// 点击头像
			handleClick(event) {
				event.stopPropagation()

				if (this.isLogin) {
					this.showMenu = !this.showMenu
				} else {
					// 未登录跳转到登录页
					this.$router.push('/')
				}
			},

			// 点击外部关闭菜单
			handleClickOutside(event) {
				if (this.showMenu && !this.$el.contains(event.target)) {
					this.showMenu = false
				}
			},

			// 打开系统设置
			handleOpenSettings() {
				console.log('打开系统设置')
				this.showMenu = false
				this.showSettingsDialog = true
				// 初始化表单数据
				this.settingsForm.username = this.userData?.user?.name || ''
				this.errors.username = ''

				// 防止页面滚动
				document.body.style.overflow = 'hidden'
			},
			handleRechare(){
				this.$router.push({
					path: '/recharge',
				});
			},
			handleUser(){
				this.$router.push({
					path: '/user',
				});
			},

			// 关闭设置弹窗
			handleCloseSettings() {
				this.showSettingsDialog = false
				this.errors.username = ''
				// 恢复页面滚动
				document.body.style.overflow = ''
			},

			// 验证用户名
			validateUsername() {
				const username = this.settingsForm.username.trim()

				if (!username) {
					this.errors.username = '请输入用户名'
					return false
				}

				if (username.length < 2 || username.length > 20) {
					this.errors.username = '用户名长度在 2 到 20 个字符'
					return false
				}

				if (!/^[\u4e00-\u9fa5a-zA-Z0-9_-]+$/.test(username)) {
					this.errors.username = '用户名只能包含中文、英文、数字、下划线和横线'
					return false
				}

				this.errors.username = ''
				return true
			},

			// 清除错误
			clearError(field) {
				this.errors[field] = ''
			},

			// 保存设置
			async handleSaveSettings() {
				console.log('保存设置')

				// 验证表单
				if (!this.validateUsername()) {
					return
				}

				this.saveLoading = true

				try {
					// 模拟API请求
					await this.updateUsername(this.settingsForm.username.trim())

					// 更新本地存储的用户信息
					if (this.userData && this.userData.user) {
						this.userData.user.name = this.settingsForm.username.trim()
						sessionStorage.setItem('currentUser', JSON.stringify(this.userData))
					}

					// 显示成功提示
					this.showSuccessMessage('用户名修改成功')
					this.handleCloseSettings()

					// 重新检查登录状态以更新显示
					this.checkLoginStatus()

				} catch (error) {
					console.error('保存设置失败:', error)
					this.showErrorMessage(error.message || '保存失败，请稍后重试')
				} finally {
					this.saveLoading = false
				}
			},

			// 模拟更新用户名的API调用
			async updateUsername(username) {
				return new Promise((resolve, reject) => {
					setTimeout(() => {
						// 模拟成功
						resolve({
							success: true
						})

						// 模拟失败
						// reject(new Error('网络错误'))
					}, 1000)
				})
			},

			// 显示成功消息
			showSuccessMessage(message) {
				// 如果有 this.$message，使用它
				if (this.$message) {
					this.$message.success(message)
				} else {
					alert('✅ ' + message)
				}
			},

			// 显示错误消息
			showErrorMessage(message) {
				// 如果有 this.$message，使用它
				if (this.$message) {
					this.$message.error(message)
				} else {
					alert('❌ ' + message)
				}
			},

			// 显示确认对话框
			async showConfirm(message, title = '提示') {
				// 如果有 this.$confirm，使用它
				if (this.$confirm) {
					return this.$confirm(message, title, {
						confirmButtonText: '确定',
						cancelButtonText: '取消',
						type: 'warning'
					})
				} else {
					return new Promise((resolve, reject) => {
						if (confirm(message)) {
							resolve()
						} else {
							reject('cancel')
						}
					})
				}
			},

			// 退出登录
			async handleLogout() {
				try {
					await this.showConfirm('确定要退出登录吗？')

					// 清除登录信息
					sessionStorage.removeItem('currentUser')
					// 清除 Vuex store 中的用户信息
					this.$store.commit('SET_USER', null)

					// 更新状态
					this.isLogin = false
					this.userData = null
					this.showMenu = false

					// 提示用户
					this.showSuccessMessage('已退出登录')
					this.$router.push({
						path: '/'
					})

				} catch (error) {
					// 用户取消操作或出错
					if (error !== 'cancel') {
						console.error('退出登录失败:', error)
						this.showErrorMessage('退出登录失败')
					}
				}
			}
		}
	}
</script>

<style scoped>
	.user-avatar-wrapper {
		position: relative;
	}

	.user-avatar {
		width: 40px;
		height: 40px;
		background: #f5f5f5;
		border: 1px solid #e0e0e0;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s;
		font-size: 14px;
		font-weight: 600;
		color: #666;
		user-select: none;
		overflow: hidden;
	}

	.user-avatar:hover {
		background: #fff;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		transform: scale(1.05);
	}

	/* 登录后的样式 */
	.user-avatar.logged-in {
		background: linear-gradient(135deg, #667eea, #764ba2);
		color: white;
		border: none;
	}

	.user-avatar.logged-in:hover {
		background: linear-gradient(135deg, #764ba2, #667eea);
	}

	/* 用户信息文本 */
	.user-info {
		font-size: 12px;
		font-weight: 500;
	}

	/* 用户菜单 */
	.user-menu {
		position: absolute;
		top: calc(100% + 12px);
		right: 0;
		background: white;
		border-radius: 8px;
		padding: 8px;
		min-width: 220px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		z-index: 2000;
		border: 1px solid #e0e0e0;
	}

	.menu-header {
		padding: 16px;
		border-bottom: 1px solid #f0f0f0;
	}

	.menu-name {
		font-weight: 600;
		color: #333;
		margin-bottom: 4px;
		font-size: 16px;
	}

	.menu-phone {
		font-size: 14px;
		color: #666;
	}

	.menu-divider {
		height: 1px;
		background: #f0f0f0;
		margin: 8px 0;
	}

	.menu-item {
		width: 100%;
		text-align: left;
		padding: 12px 16px;
		background: none;
		border: none;
		cursor: pointer;
		font-size: 15px;
		color: #333;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		gap: 12px;
		border-radius: 6px;
		font-family: inherit;
		margin-bottom: 4px;
	}

	.menu-item:hover {
		background: #f5f5f5;
	}

	.menu-item.setting-item:hover {
		background: #e3f2fd;
		color: #1976d2;
	}

	.menu-item.logout-item:hover {
		background: #ffebee;
		color: #f44336;
	}

	/* 菜单动画 */
	.menu-enter-active,
	.menu-leave-active {
		transition: all 0.2s ease;
		transform-origin: top right;
	}

	.menu-enter,
	.menu-leave-to {
		opacity: 0;
		transform: scale(0.95) translateY(-10px);
	}

	/* 模态框样式 */
	.modal-overlay {
		height: 100vh;
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
		padding: 20px;
	}

	.modal-dialog {
		background: white;
		border-radius: 8px;
		width: 100%;
		max-width: 400px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
		overflow: hidden;
	}

	.modal-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 20px;
		border-bottom: 1px solid #f0f0f0;
	}

	.modal-title {
		font-size: 18px;
		font-weight: 600;
		color: #333;
		margin: 0;
	}

	.modal-close {
		background: none;
		border: none;
		cursor: pointer;
		padding: 4px;
		color: #999;
		transition: color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.modal-close:hover {
		color: #333;
	}

	.modal-body {
		padding: 20px;
	}

	.modal-footer {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
		padding: 15px 20px;
		border-top: 1px solid #f0f0f0;
	}

	/* 表单样式 */
	.settings-form {
		margin: 0;
	}

	.form-group {
		margin-bottom: 20px;
	}

	.form-label {
		display: flex;
		align-items: center;
		margin-bottom: 8px;
		font-size: 14px;
		font-weight: 500;
		color: #333;
	}

	.label-text {
		margin-right: 4px;
	}

	.label-required {
		color: #f56c6c;
	}

	.input-wrapper {
		position: relative;
		display: flex;
		align-items: center;
	}

	.input-prefix {
		position: absolute;
		left: 12px;
		color: #999;
		pointer-events: none;
	}

	.form-input {
		width: 100%;
		padding: 10px 12px 10px 36px;
		border: 1px solid #dcdfe6;
		border-radius: 4px;
		font-size: 14px;
		color: #333;
		transition: border-color 0.2s;
		font-family: inherit;
	}

	.form-input:focus {
		outline: none;
		border-color: #409eff;
	}

	.form-input.error {
		border-color: #f56c6c;
	}

	.char-count {
		position: absolute;
		right: 12px;
		font-size: 12px;
		color: #999;
	}

	.error-message {
		color: #f56c6c;
		font-size: 12px;
		margin-top: 4px;
	}

	.form-tip {
		font-size: 12px;
		color: #909399;
		margin-top: 5px;
		line-height: 1.4;
	}

	/* 按钮样式 */
	.btn {
		padding: 9px 20px;
		border: none;
		border-radius: 4px;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
		font-family: inherit;
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.btn-default {
		background: #fff;
		color: #606266;
		border: 1px solid #dcdfe6;
	}

	.btn-default:hover {
		color: #409eff;
		border-color: #c6e2ff;
		background: #ecf5ff;
	}

	.btn-primary {
		background: #409eff;
		color: white;
	}

	.btn-primary:hover:not(:disabled) {
		background: #66b1ff;
	}

	.btn-primary:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	/* 加载动画 */
	.loading-spinner {
		display: inline-block;
		width: 14px;
		height: 14px;
		border: 2px solid #ffffff;
		border-right-color: transparent;
		border-radius: 50%;
		animation: spin 0.6s linear infinite;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	/* 模态框动画 */
	.modal-enter-active,
	.modal-leave-active {
		transition: all 0.3s ease;
	}

	.modal-enter,
	.modal-leave-to {
		opacity: 0;
	}

	.modal-enter .modal-dialog,
	.modal-leave-to .modal-dialog {
		transform: scale(0.9);
	}

	/* 移动端适配 */
	@media (max-width: 768px) {
		.user-avatar {
			width: 36px;
			height: 36px;
		}

		.user-menu {
			right: -10px;
			min-width: 200px;
		}

		.menu-header {
			padding: 12px;
		}

		.menu-item {
			padding: 10px 12px;
			font-size: 14px;
		}

		.modal-dialog {
			max-width: calc(100vw - 40px);
		}

		.modal-header {
			padding: 16px;
		}

		.modal-body {
			padding: 16px;
		}
	}
</style>